import {
  BLOCKS,
  type Document as RichTextDocument,
  type TopLevelBlock,
} from '@contentful/rich-text-types';

import GenericComponent, {
  type SectionContent,
} from '@/components/GenericComponent/GenericComponent';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import RichText from '@/components/RichText/RichText';

import useStyles from './BlogRichText.styles';

interface BlogRichTextContent {
  text: RichTextDocument;
}

// The following two functions are here for the purpose of separating the rich text
// document by the embedded content blocks, so that the embedded content can be
// rendered outside of the context of Contentful's rich text renderer. So, instead
// of one rich text document, we have multiple rich text documents interspersed
// with embedded content blocks. We map through these to conditionally render either
// <RichText ... /> or <GenericComponent ... />.
export const makeRichTextDocument = (
  blocks: TopLevelBlock[]
): RichTextDocument => {
  return {
    content: blocks,
    data: {},
    nodeType: BLOCKS.DOCUMENT,
  };
};

export const makeSections = (
  blocks: TopLevelBlock[]
): Array<RichTextDocument | TopLevelBlock> => {
  let textBlocks: TopLevelBlock[] = [];
  const sections: Array<RichTextDocument | TopLevelBlock> = [];

  blocks?.forEach((block: TopLevelBlock) => {
    if (block.nodeType === BLOCKS.EMBEDDED_ENTRY) {
      sections.push(makeRichTextDocument(textBlocks));
      sections.push(block);
      textBlocks = [];
    } else {
      textBlocks.push(block);
    }
  });
  sections.push(makeRichTextDocument(textBlocks));

  return sections;
};

export default function BlogRichText({
  text,
}: BlogRichTextContent): React.JSX.Element {
  const { classes } = useStyles();

  const indexedDocument = makeRichTextDocument(
    text?.content?.map((block: TopLevelBlock, i: number) => ({
      ...block,
      index: i,
    }))
  );

  return (
    <div>
      {makeSections(indexedDocument?.content).map(
        (block: TopLevelBlock | RichTextDocument, i: number) => {
          if (block.nodeType === BLOCKS.DOCUMENT) {
            return (
              <GridContainer2
                fixed
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                key={i}
                gridProps={{ className: classes.fixedGrid }}
              >
                <div className={classes.blogRichText}>
                  <GridContainer2
                    fixed
                    gridProps={{ className: classes.fixedGrid }}
                  >
                    <RichText
                      content={block}
                      blockClasses={{
                        p: classes.richTextParagraph,
                        ul: classes.richTextList,
                        ol: classes.richTextList,
                      }}
                    />
                  </GridContainer2>
                </div>
              </GridContainer2>
            );
          } else {
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
              <div key={i} className={classes.richTextContent}>
                <GenericComponent
                  // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                  key={i}
                  content={block.data.target as SectionContent}
                />
              </div>
            );
          }
        }
      )}
    </div>
  );
}
